import { Space, Flex, Divider, Dropdown, Tooltip, Row, Col } from "antd"
import { Text, Title } from "ui/Typography"
import styles from "./index.module.css"
import { useTranslation } from "react-i18next"
import MoreActionEllipsis from "ui/MoreActionEllipsis"
import PropTypes from "prop-types"
import { displayPaymentStatus, BOOKING_REQUEST_TYPES } from "../../utils"
import StatusTag from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import { useMemo, useState } from "react"
import RecordPaymentModal from "./Modals/RecordPayment"
import AdjustBookingPriceModal from "./Modals/AdjustBookingPrice"
import { Icon } from "ui/Icon"
import { numberToCurrency } from "helpers/string"
import useSummaryDetails from "./hooks/useSummaryDetails"
import { spacings } from "themes/variables"
import SummaryKeyValue from "./SummaryKeyValue"
import CollectPaymentModal from "./Modals/CollectPayment"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const PaymentSummary = ({ paymentSummary, payment_status, hasPayments, bookingUUID, bookingId, bookingStatus, pricing }) => {
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false)
  const [isCollectPaymentModalOpen, setIsCollectPaymentModalOpen] = useState(false)
  const [isAdjustBookingPriceModalOpen, setIsAdjustBookingPriceModalOpen] = useState(false)
  const summaryDetails = useSummaryDetails(paymentSummary, pricing)
  const { total_price, remaining_balance, adjust_price_history } = paymentSummary ?? {}
  const canEditBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.EDIT)

  const handleCloseRecordPaymentModal = () => setIsRecordPaymentModalOpen(false)
  const handleCloseCollectPaymentModal = () => setIsCollectPaymentModalOpen(false)
  const handleCloseAdjustBookingPriceModal = () => setIsAdjustBookingPriceModalOpen(false)

  const payTitle = hasPayments ? t("client.client_booking.total") : t("client.client_booking.total_to_pay")
  const isUpcomingOrCompleted =
    bookingStatus?.toLowerCase() === BOOKING_REQUEST_TYPES.UPCOMING || bookingStatus?.toLowerCase() === BOOKING_REQUEST_TYPES.COMPLETED

  const dropdownItems = useMemo(() => {
    const items = [
      {
        key: "record_payment",
        label: "Record Payment",
        onClick: () => setIsRecordPaymentModalOpen(true),
        disabled: paymentSummary?.payment_option === "full_amount_by_link"
      },
      {
        key: "collect_payment",
        label: "Collect Payment",
        onClick: () => setIsCollectPaymentModalOpen(true),
        disabled: false
      }
    ]
    if (canEditBooking) {
      items.push({
        key: "adjust_booking_price",
        label: "Adjust Booking Price",
        onClick: () => setIsAdjustBookingPriceModalOpen(true),
        disabled: false
      })
    }

    return items
  }, [paymentSummary?.payment_option, canEditBooking])

  const remainingBalValue = numberToCurrency(remaining_balance)

  return (
    <>
      <Space direction="vertical" className={`${styles.paymentSummaryContainer} w-full p-xl`}>
        <Flex gap={4} justify="space-between" className="mb-l" align="center">
          <Flex gap={4} vertical>
            <Title level={4}>{t("client.payment.labels.payment_summary")}</Title>
            {payment_status && <StatusTag status={displayPaymentStatus(payment_status)} bookingStatus={bookingStatus} />}
          </Flex>
          {isUpcomingOrCompleted && (
            <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items: dropdownItems }}>
              <MoreActionEllipsis />
            </Dropdown>
          )}
        </Flex>
        <Flex vertical gap={spacings.XS}>
          <SummaryKeyValue details={summaryDetails} />
          {adjust_price_history?.map((item, idx) => {
            const { price_difference, reason } = item || {}
            return (
              <Row key={item?.id + idx}>
                <Col xs={14} md={12}>
                  <Text>
                    Price Change{" "}
                    <Tooltip title={reason}>
                      <Icon type="feather" name="Info" width={16} height={16} />
                    </Tooltip>
                  </Text>
                </Col>
                <Col xs={10} md={12}>
                  <Text fw={500}>
                    &nbsp;{price_difference > 0 ? "+" : ""}
                    {numberToCurrency(price_difference)}
                  </Text>
                </Col>
              </Row>
            )
          })}
          <Divider size="small" className="m-0" />
          <Row>
            <Col xs={14} md={12}>
              <Text fw={hasPayments ? 400 : 700}>{payTitle}</Text>
            </Col>
            <Col xs={10} md={12}>
              <Text fw={hasPayments ? 400 : 700}>&nbsp;&nbsp;&nbsp;{numberToCurrency(total_price)}</Text>
            </Col>
          </Row>
          {hasPayments && (
            <Row>
              <Col xs={14} md={12}>
                <Text fw={700}>{t("client.client_booking.remaining_balance")}</Text>
              </Col>
              <Col xs={10} md={12}>
                <Text fw={700}>&nbsp;&nbsp;&nbsp;{remainingBalValue}</Text>
              </Col>
            </Row>
          )}
        </Flex>
      </Space>
      {isRecordPaymentModalOpen && (
        <RecordPaymentModal
          remainingBalance={hasPayments ? remaining_balance : total_price}
          bookingUUID={bookingUUID}
          bookingId={bookingId}
          onOpen={isRecordPaymentModalOpen}
          onClose={handleCloseRecordPaymentModal}
        />
      )}
      {isCollectPaymentModalOpen && (
        <CollectPaymentModal
          remainingBalance={hasPayments ? remaining_balance : total_price}
          bookingUUID={bookingUUID}
          bookingId={bookingId}
          onOpen={isCollectPaymentModalOpen}
          onClose={handleCloseCollectPaymentModal}
        />
      )}
      {isAdjustBookingPriceModalOpen && (
        <AdjustBookingPriceModal
          bookingId={bookingId}
          bookingUUID={bookingUUID}
          currentPrice={total_price}
          onOpen={isAdjustBookingPriceModalOpen}
          onClose={handleCloseAdjustBookingPriceModal}
        />
      )}
    </>
  )
}

PaymentSummary.propTypes = {
  paymentSummary: PropTypes.shape({
    total: PropTypes.number,
    tax: PropTypes.number,
    adult: PropTypes.shape({
      count: PropTypes.number,
      price: PropTypes.number
    }),
    child: PropTypes.shape({
      count: PropTypes.number,
      price: PropTypes.number
    }),
    infant: PropTypes.shape({
      count: PropTypes.number,
      price: PropTypes.number
    }),
    remaining_balance: PropTypes.number,
    total_person_price: PropTypes.number,
    total_child_price: PropTypes.number,
    total_infant_price: PropTypes.number,
    child_allowed: PropTypes.bool,
    infants_allowed: PropTypes.bool,
    payment_option: PropTypes.string
  }),
  payment_status: PropTypes.string,
  hasPayments: PropTypes.bool,
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  bookingStatus: PropTypes.string,
  pricing: PropTypes.object
}

export default PaymentSummary
